import React, { useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Select, Space, Spin, Tag, Typography } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { formatDdMmYyyy } from '../../utils/trainingModel';
import { controlRefPlaceholder, parseTags, standardOptionsFrom } from '../../utils/libraryTemplateModel';

const { Text, Title, Paragraph } = Typography;

/**
 * Project-scoped consumption read for Advisor / Lead Advisor (the Stage-7 drafting personas):
 *   GET /projects/:id/library-templates/:controlRef?standardId=
 * The API membership-checks the project, returns ONLY the current published version, and on
 * first use records which version this project's control document was based on (so a later
 * library update never silently changes it). `firstConsumption` is reported to the user.
 *
 * "No published template" is a normal 404 and is shown as an empty state with that control
 * named — never as a generic failure. Template text is rendered as plain text, never markup.
 * ISO/IEC 27002:2022 A.5.37 (documented operating procedures).
 */
const LibraryTemplateLookup = ({ projectId, projectName }) => {
    const { request } = useApiRequest();
    const [standardId, setStandardId] = useState('iso.27001-2022');
    const [controlRefInput, setControlRefInput] = useState('');
    const [query, setQuery] = useState(null);

    const standards = useResource(
        async () => (await request('GET', '/standards')).data.standards || [],
        [request],
    );
    const options = useMemo(() => standardOptionsFrom(standards.data), [standards.data]);

    const result = useResource(
        async () => {
            try {
                const { data } = await request('GET', `/projects/${projectId}/library-templates/${encodeURIComponent(query.controlRef)}`, {
                    query: { standardId: query.standardId },
                });
                return { found: true, template: data.template, firstConsumption: data.firstConsumption };
            } catch (err) {
                if (err.status === 404 && /library template/i.test(err.message || '')) return { found: false, message: err.message };
                throw err;
            }
        },
        [projectId, query, request],
        { enabled: !!query },
    );

    const search = () => {
        if (!standardId || !controlRefInput.trim()) return;
        setQuery({ standardId, controlRef: controlRefInput.trim() });
    };

    const t = result.data?.template;

    return (
        <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Look up the current published library template for a control{projectName ? ` in ${projectName}` : ''}. If this project already has a control document for the control, the first lookup records which template version it was based on.
            </Text>
            <Space wrap style={{ marginBottom: 16 }}>
                <Select
                    aria-label="Standard"
                    showSearch
                    optionFilterProp="label"
                    style={{ minWidth: 260 }}
                    value={standardId}
                    onChange={setStandardId}
                    options={options}
                    loading={standards.status === 'loading'}
                />
                <Input
                    aria-label="Control reference"
                    style={{ width: 220 }}
                    value={controlRefInput}
                    onChange={(e) => setControlRefInput(e.target.value)}
                    onPressEnter={search}
                    placeholder={controlRefPlaceholder(standardId)}
                />
                <Button type="primary" onClick={search} disabled={!controlRefInput.trim()}>Fetch template</Button>
            </Space>
            {standards.status === 'error' && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Could not load the standards list" action={<Button size="small" onClick={standards.reload}>Retry</Button>} />
            )}

            {!query && <Empty description="Choose a standard and a control to fetch its template." />}
            {query && result.status === 'loading' && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
            {query && result.status === 'error' && (
                <Alert type="error" showIcon message="Could not fetch the template" description={result.error?.message} action={<Button size="small" onClick={result.reload}>Retry</Button>} />
            )}
            {query && result.status === 'ready' && result.data && !result.data.found && (
                <Empty description={`No published library template for ${query.controlRef} yet.`} />
            )}
            {query && result.status === 'ready' && t && (
                <div data-testid="library-template-result">
                    <Space wrap style={{ marginBottom: 6 }}>
                        <Title level={5} style={{ margin: 0 }}>{t.controlRef} - {t.title}</Title>
                        <Tag color="green">v{t.version}</Tag>
                        {t.family && <Tag>{t.family}</Tag>}
                        {parseTags(t.tagsJson).map((tag) => <Tag key={tag}>{tag}</Tag>)}
                    </Space>
                    <div style={{ marginBottom: 8 }}>
                        <Text type="secondary">
                            Published {formatDdMmYyyy(t.publishedAt)}
                            {t.sourceType === 'ai_generated' ? ' · began as an AI draft; a platform curator reviewed and approved it before publication' : ''}
                        </Text>
                    </div>
                    {result.data.firstConsumption && (
                        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={`Recorded: this project's ${t.controlRef} document is based on library template v${t.version}.`} />
                    )}
                    {t.bodyText ? (
                        <div style={{ background: 'var(--color-surface-sunken)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 14, maxHeight: 480, overflow: 'auto' }}>
                            <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{t.bodyText}</Paragraph>
                        </div>
                    ) : (
                        <Alert type="warning" showIcon message="This published template has no text body." description={t.templateBlobUrl ? 'It exists as a file reference only; the file is not downloadable from here.' : undefined} />
                    )}
                </div>
            )}
        </div>
    );
};

export default LibraryTemplateLookup;
